import React from "react";
import { Document, Page, Text, View, Image, Font } from "@react-pdf/renderer";
import { orderInvoiceStyles } from "./styles";
import { OrderInvoiceLogoImg } from "../../lib/images";
import { USER_ROLE } from "../../lib/constants";

// Define custom fonts if needed
Font.register({
  family: "Helvetica",
  src: "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/fonts/fa-solid-900.ttf",
});

const Invoice: React.FC<any> = ({
  customerName,
  customerPhoneNumber,
  deliveryAddress,
  orderItems,
  serviceFee,
  paymentProcessingFee,
  totalAmount,
  customerNote,
  vendorNote,
  id,
  userRole,
  deliveryFee,
  oldDeliveryFee,
  oldTotalAmount,
  isShowTransactionFee,
  isShowDeliveryFee = true,
}) => (
  <Document>
    <Page size="A4" style={orderInvoiceStyles.page}>
      {/* Header with Logo */}
      <View style={orderInvoiceStyles.header}>
        <Image src={OrderInvoiceLogoImg} style={orderInvoiceStyles.logo} />
        <View style={orderInvoiceStyles.titleContainer}>
          <Text style={orderInvoiceStyles.title}>Order Invoice</Text>
          <Text style={orderInvoiceStyles.subtitle}>
            Date: {new Date().toLocaleDateString()}
          </Text>
          <Text style={orderInvoiceStyles.subtitle}>Order Number: #{id}</Text>
        </View>
      </View>

      {/* Customer Details */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <View>
          <Text style={orderInvoiceStyles.sectionTitle}>
            Customer Information
          </Text>
          <Text style={orderInvoiceStyles.companyDetails}>
            Name: {customerName}
          </Text>
          <Text style={orderInvoiceStyles.companyDetails}>
            Phone: {customerPhoneNumber}
          </Text>
          <Text style={orderInvoiceStyles.companyDetails}>
            Address: {deliveryAddress}
          </Text>
        </View>
      </View>

      {/* Product Details Table */}
      <View>
        <Text style={orderInvoiceStyles.sectionTitle}>Order Details</Text>
        <View style={orderInvoiceStyles.tableHeader}>
          <Text style={[orderInvoiceStyles.tableCol, { width: "5%" }]}>#</Text>
          <Text
            style={[
              orderInvoiceStyles.tableCol,
              { width: "40%", textAlign: "left" },
            ]}
          >
            Product
          </Text>
          <Text style={orderInvoiceStyles.tableCol}>Price</Text>
          <Text style={orderInvoiceStyles.tableCol}>Qty.</Text>
          <Text style={[orderInvoiceStyles.tableCol, { textAlign: "right" }]}>
            Subtotal
          </Text>
        </View>

        {orderItems.map((item: any, index: any) => (
          <View key={index} style={orderInvoiceStyles.tableRow}>
            <Text style={[orderInvoiceStyles.tableCol, { width: "5%" }]}>
              {index + 1}
            </Text>
            <Text
              style={[
                orderInvoiceStyles.tableCol,
                { width: "40%", textAlign: "left" },
              ]}
            >
              {item.productName}
            </Text>
            <Text style={orderInvoiceStyles.tableCol}>
              ${item.price.toFixed(2)}
            </Text>
            <Text style={orderInvoiceStyles.tableCol}>{item.quantity}</Text>
            <Text style={[orderInvoiceStyles.tableCol, { textAlign: "right" }]}>
              ${(item.price * item.quantity).toFixed(2)}
            </Text>
          </View>
        ))}
      </View>

      {/* Order Summary Section */}
      <View style={orderInvoiceStyles.orderSummary} wrap={false}>
        <View style={[orderInvoiceStyles.summaryRow, { marginBottom: 5 }]}>
          <Text style={orderInvoiceStyles.summaryText}>
            Subtotal ({orderItems.length}):
          </Text>
          <Text style={orderInvoiceStyles.summaryText}>
            $
            {orderItems
              .reduce(
                (sum: any, item: any) => sum + item.price * item.quantity,
                0
              )
              ?.toFixed(2) || 0.0}
          </Text>
        </View>
        <View style={orderInvoiceStyles.summaryRow}>
          <Text style={orderInvoiceStyles.summaryText}>
            {userRole === USER_ROLE.ADMIN
              ? "Customer Service Fee:"
              : "Service Fee:"}
          </Text>
          <Text style={orderInvoiceStyles.summaryText}>
            {userRole === USER_ROLE.VENDOR ? "-" : ""}$
            {serviceFee?.toFixed(2) || 0.0}
          </Text>
        </View>
        {(userRole === USER_ROLE.ADMIN || USER_ROLE.VENDOR) &&
          isShowTransactionFee && (
            <View style={orderInvoiceStyles.summaryRow}>
              <Text style={orderInvoiceStyles.summaryText}>
                Transaction Fee:
              </Text>
              <Text style={orderInvoiceStyles.summaryText}>
                - ${paymentProcessingFee?.toFixed(2) || 0.0}
              </Text>
            </View>
          )}
        {isShowDeliveryFee && (
          <View style={orderInvoiceStyles.summaryRow}>
            <Text style={orderInvoiceStyles.summaryText}>Delivery Fee:</Text>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                rowGap: 5,
              }}
            >
              {oldDeliveryFee && (
                <Text
                  style={[
                    orderInvoiceStyles.summaryText,
                    {
                      textDecoration: "line-through",
                      color: "red",
                      fontSize: 8,
                      marginLeft: 2,
                    },
                  ]}
                >
                  (${oldDeliveryFee.toFixed(2)})
                </Text>
              )}
              <Text style={[orderInvoiceStyles.summaryText]}>
                {" "}
                ${deliveryFee?.toFixed(2) || "0.00"}
              </Text>
            </View>
          </View>
        )}

        <View style={orderInvoiceStyles.summaryRow}>
          <Text style={orderInvoiceStyles.grandTotal}>Total:</Text>
          <Text
            style={[
              orderInvoiceStyles.grandTotal,
              {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              },
            ]}
          >
            {oldTotalAmount && (
              <Text
                style={[
                  orderInvoiceStyles.summaryText,
                  {
                    textDecoration: "line-through",
                    color: "red",
                    fontSize: 8,
                    marginRight: 5,
                  },
                ]}
              >
                (${parseFloat(oldTotalAmount)?.toFixed(2)})
              </Text>
            )}
            <Text
              style={[
                {
                  marginLeft: 4,
                },
              ]}
            >
              {" "}
              $
              {userRole === USER_ROLE.ADMIN && isShowTransactionFee
                ? (totalAmount - paymentProcessingFee)?.toFixed(2)
                : userRole === USER_ROLE.VENDOR
                ? parseFloat(totalAmount)?.toFixed(2)
                : totalAmount?.toFixed(2)}
            </Text>
          </Text>
        </View>
      </View>

      {/* Notes Section */}
      <View style={orderInvoiceStyles.notesSection} wrap={false}>
        <Text style={orderInvoiceStyles.notesTitle}>Customer Note:</Text>
        <Text style={orderInvoiceStyles.notesText}>{customerNote}</Text>
      </View>
      <View style={orderInvoiceStyles.notesSection} wrap={false}>
        <Text style={orderInvoiceStyles.notesTitle}>Vendor Note:</Text>
        <Text style={orderInvoiceStyles.notesText}>{vendorNote}</Text>
      </View>

      {/* Footer */}
      <View style={orderInvoiceStyles.footer}>
        <Text>Thank you for your business!</Text>
      </View>
    </Page>
  </Document>
);

export default Invoice;
